/**
 * Transform CDN image URLs between different resize presets.
 *
 * CDN (cdn-v2.kidsplaza.vn) handles on-the-fly resize via {width}x{height}/media/ pattern.
 * Backend GraphQL returns media_gallery URLs at 700×700 (product_page_image_medium).
 * Frontend derives other sizes by replacing the size prefix in the URL.
 *
 * Sizes match Magento view.xml (KidsPlaza/Theme):
 *   thumbnail:  75×75   (product_thumbnail_image)  — gallery nav thumbs
 *   small:     202×202  (category_page_grid)       — catalog grid
 *   medium:    700×700  (product_page_image_medium) — gallery main slide
 *   original:  no prefix                           — zoom / full view
 */

const CDN_SIZE_PATTERN = /(\d+)x(\d+)\/media\//;

export type CdnImageSize = 'thumbnail' | 'small' | 'medium' | 'original';

const SIZE_DIMENSIONS: Record<Exclude<CdnImageSize, 'original'>, string> = {
    thumbnail: '75x75',
    small: '202x202',
    medium: '700x700',
};

/**
 * Transform a CDN image URL to a different size.
 *
 * @param url  - Image URL (may or may not contain a size prefix)
 * @param size - Target size
 * @returns Transformed URL, or original URL if pattern not found
 *
 * @example
 * // API returns: https://cdn-v2.kidsplaza.vn/700x700/media/catalog/product/a/b/image.jpg
 * getCdnImageUrl(url, 'thumbnail')  → .../75x75/media/catalog/product/a/b/image.jpg
 * getCdnImageUrl(url, 'original')   → .../media/catalog/product/a/b/image.jpg
 */
export const getCdnImageUrl = (url: string, size: CdnImageSize): string => {
    if (!url) return url;

    if (size === 'original') {
        return url.replace(CDN_SIZE_PATTERN, 'media/');
    }

    const dimensions = SIZE_DIMENSIONS[size];

    if (CDN_SIZE_PATTERN.test(url)) {
        return url.replace(CDN_SIZE_PATTERN, `${dimensions}/media/`);
    }

    // URL has no size prefix — add one
    return url.replace('media/', `${dimensions}/media/`);
};

